import { Injectable, OnDestroy } from "@angular/core";
import { Router } from "@angular/router";
import { BillingInterval, Role, SubscriptionTier } from "@types";
import { exponentialRetry, isHttpErrorResponse, waitForOnline } from "@utils";
import { OAuthEvent, OAuthInfoEvent, OAuthService } from "angular-oauth2-oidc";
import { authCodeFlowConfig } from "app/auth-code-flow.config";
import { environment } from "environments/environment";
import { JWTPayload, decodeJwt } from "jose";
import {
  BehaviorSubject,
  Observable,
  defer,
  firstValueFrom,
  merge,
  of,
} from "rxjs";
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  shareReplay,
  tap,
  take,
} from "rxjs/operators";
import { ApplicationInsightsService } from "./application-insights.service";
import {
  RefreshFailure,
  classifyRefreshFailure,
  getOAuthErrorCode,
  isAbortError,
} from "./refresh-token-error";


/**
 * How long a caller waits for another tab or another caller in this tab to
 * finish its refresh before giving up on the lock.
 *
 * This bounds waiting for the lock, not the token request itself, so it has to
 * be comfortably longer than a slow token request. A device waking from sleep
 * can spend many seconds on DNS and TLS before the request even starts, and
 * abandoning the wait too early turns one slow refresh into a failure for
 * every other caller queued behind it.
 */
const lockWaitTimeoutMilliseconds = 60000;

/**
 * How long to wait for the network to come back before attempting a refresh.
 *
 * Kept short because it is paid on every attempt: a refresh that waits too long
 * leaves every caller joined to it hanging, which is worse for the user than a
 * refresh that fails and is retried.
 */
const onlineWaitTimeoutMilliseconds = 10000;

const refreshRetryCount = 4;
const refreshRetryMaxDelayMilliseconds = 8000;

export interface LoginOptions {
  disableClearingLocalStorageBeforeLogin?: boolean;
}

interface RefreshLifecycle {
  refresh$: Observable<void>;
}

@Injectable({
  providedIn: "root",
})
export class AuthService implements OnDestroy {
  private userIdSubject = new BehaviorSubject<string | null>(null);
  public userId$ = this.userIdSubject
    .asObservable()
    .pipe(distinctUntilChanged());

  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  public isAuthenticated$ = this.isAuthenticatedSubject
    .asObservable()
    .pipe(distinctUntilChanged());

  private isDepartmentAdminSubject = new BehaviorSubject<boolean>(false);
  public isDepartmentAdmin$ = this.isDepartmentAdminSubject
    .asObservable()
    .pipe(distinctUntilChanged());

  private isBroadcasterSubject = new BehaviorSubject<boolean>(false);
  public isBroadcaster$ = this.isBroadcasterSubject
    .asObservable()
    .pipe(distinctUntilChanged());

  private dataRegionSubject = new BehaviorSubject<string | null>(null);
  public dataRegion$ = this.dataRegionSubject
    .asObservable()
    .pipe(distinctUntilChanged());

  private storageListener: ((event: StorageEvent) => void) | null = null;

  private onboardingDataRegion: string | null = null;

  private refreshLifecycle: RefreshLifecycle | null = null;

  private hasCompletedOnboardingSubject = new BehaviorSubject<boolean>(false);
  public hasCompletedOnboarding$ = this.hasCompletedOnboardingSubject.pipe(
    distinctUntilChanged()
  );

  public constructor(
    private oauthService: OAuthService,
    private appInsightsService: ApplicationInsightsService,
    private router: Router
  ) {
    this.oauthService.events.subscribe({
      next: (oauthEvent) => this.handleOAuthEvent(oauthEvent),
    });

    this.storageListener = (event: StorageEvent) => {
      if (event.key !== "access_token") return;
      const isAuthenticated = this.oauthService.hasValidAccessToken();
      this.isAuthenticatedSubject.next(isAuthenticated);
      if (this.isAuthenticated()) return;
      this.router.navigate(["/"]);
    };

    window.addEventListener("storage", this.storageListener);

    this.updateState();
  }

  private saveToLocalStorage(key: string, value: string) {
    if (!key || !value) return;
    localStorage.setItem(key, value);
  }

  private getParamFromUrl(param: string): string | null {
    try {
      const url = new URL(location.href);
      const code = url.searchParams.get(param);
      return code ?? null;
    } catch (err) {
      return null;
    }
  }

  public getInvitationCode(): string | null {
    const invitationCode = localStorage.getItem("invitation_code");
    return invitationCode ?? null;
  }

  public getProductTrialType(): SubscriptionTier | null {
    const productTrialType = localStorage
      .getItem("product_trial_type")
      ?.toLowerCase();

    for (const tier of Object.values(SubscriptionTier)) {
      if (tier.toLowerCase() !== productTrialType) continue;
      return tier;
    }

    return null;
  }

  public getBillingInterval(): BillingInterval | null {
    const trialBillingInterval = localStorage
      .getItem("billing_interval")
      ?.toLowerCase();

    for (const billingInterval of Object.values(BillingInterval)) {
      if (billingInterval.toLowerCase() !== trialBillingInterval) continue;
      return billingInterval;
    }

    return null;
  }

  public getIsOnboarding(): string | null {
    return localStorage.getItem("is_onboarding");
  }

  public removeIsOnboarding() {
    localStorage.removeItem("is_onboarding");
  }

  public removeInvitationCode() {
    localStorage.removeItem("invitation_code");
  }

  public getOnboardInfo() {
    return localStorage.getItem("onboard_info");
  }

  private log(...args: unknown[]): void {
    if (environment.production) return;
    // eslint-disable-next-line no-console
    console.log("[Auth]", ...args);
  }

  // One-way, truncated hash so logs can identify a refresh token without exposing it.
  private async getRefreshTokenFingerprint(): Promise<string> {
    const token = this.oauthService.getRefreshToken();
    if (!token) return "none";
    try {
      const digest = await crypto.subtle.digest(
        "SHA-256",
        new TextEncoder().encode(token)
      );
      return Array.from(new Uint8Array(digest))
        .map((byte) => byte.toString(16).padStart(2, "0"))
        .join("")
        .slice(0, 12);
    } catch {
      return "error";
    }
  }

  public refreshToken(): Observable<void> {
    if (this.refreshLifecycle) {
      this.log("refreshToken: joining in-flight refresh");
      return this.refreshLifecycle.refresh$;
    }

    let lifecycle: RefreshLifecycle;
    const refresh$ = defer(() => this.attemptRefresh()).pipe(
      exponentialRetry<void>(
        refreshRetryCount,
        refreshRetryMaxDelayMilliseconds,
        (error) => classifyRefreshFailure(error) === "retryable"
      ),
      tap({
        complete: () => this.detachRefreshLifecycle(lifecycle),
        error: (error: unknown) => {
          this.detachRefreshLifecycle(lifecycle);
          this.handleRefreshFailure(error);
        },
      }),
      finalize(() => {
        this.detachRefreshLifecycle(lifecycle);
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );
    lifecycle = {
      refresh$,
    };
    this.refreshLifecycle = lifecycle;

    return refresh$;
  }

  private detachRefreshLifecycle(lifecycle: RefreshLifecycle): void {
    if (this.refreshLifecycle === lifecycle) {
      this.refreshLifecycle = null;
    }
  }

  /**
   * Runs one refresh attempt, serialized across tabs by the `refreshToken`
   * lock so that the single-use refresh token is never sent twice.
   */
  private async attemptRefresh(): Promise<void> {
    await waitForOnline(onlineWaitTimeoutMilliseconds);

    const tokenBeforeLock = this.oauthService.getAccessToken();

    // Note: don't use hasValidAccessToken() to decide whether a refresh is
    // needed — it treats a token as valid until well after expiry (default
    // clock skew), which would skip the proactive (token_expires) refresh and
    // needed reactive refreshes. Compare the token itself instead.
    const hasTokenRotated = () =>
      this.oauthService.getAccessToken() !== tokenBeforeLock;

    if (!navigator.locks) {
      // Web Locks are unavailable outside a secure context. Callers in this
      // tab are still deduped by the refresh lifecycle, but nothing serializes
      // across tabs, so two tabs can send the same single-use token and
      // invalidate the family. Refreshing unlocked still beats failing every
      // refresh outright, and production origins are always https.
      this.log("refreshToken: Web Locks unavailable, refreshing without lock");
      await this.requestNewToken();
      return;
    }

    this.log("refreshToken: requesting lock");

    try {
      await navigator.locks.request(
        "refreshToken",
        { signal: AbortSignal.timeout(lockWaitTimeoutMilliseconds) },
        async () => {
          if (hasTokenRotated()) {
            this.log(
              "refreshToken: skipped, token already rotated by another caller"
            );
            return;
          }
          await this.requestNewToken();
        }
      );
    } catch (error) {
      // Giving up on the lock is not a refresh failure. Another caller very
      // likely holds the lock precisely because it is refreshing, so if the
      // token has since rotated this caller already has what it needed.
      if (isAbortError(error) && hasTokenRotated()) {
        this.log(
          "refreshToken: lock wait abandoned, but token was rotated meanwhile"
        );
        return;
      }
      throw error;
    }
  }

  private async requestNewToken(): Promise<void> {
    const refreshTokenFingerprint = await this.getRefreshTokenFingerprint();
    const refreshStartedAt = performance.now();
    this.log("refreshToken: refreshing access token", {
      refreshTokenFingerprint,
      elapsedMilliseconds: 0,
    });
    this.appInsightsService.trackTrace("refreshing access token", {
      refreshTokenFingerprint,
    });
    try {
      await this.oauthService.refreshToken();
      this.log("refreshToken: refresh succeeded", {
        refreshTokenFingerprint,
        elapsedMilliseconds: performance.now() - refreshStartedAt,
      });
    } catch (error) {
      this.log("refreshToken: refresh failed", {
        refreshTokenFingerprint,
        elapsedMilliseconds: performance.now() - refreshStartedAt,
        error,
      });
      throw error;
    }
  }

  /**
   * Signs the user out only when the auth server said the grant is no longer
   * valid. The refresh token can only be used once, so a failure for any other
   * reason leaves a token that is still usable and must not end the session.
   */
  private handleRefreshFailure(error: unknown): RefreshFailure {
    const classification = classifyRefreshFailure(error);

    this.appInsightsService.trackTrace("token refresh failed", {
      classification,
      oauthErrorCode: getOAuthErrorCode(error) ?? "none",
      httpStatus: `${this.getHttpStatus(error)}`,
    });

    this.log("refreshToken: classified failure", { classification, error });

    if (classification !== "session-ended") return classification;

    this.logout("Refresh token rejected by the auth server", {
      clientLogout: true,
    });
    console.error("Token refresh failed", error);

    return classification;
  }

  private getHttpStatus(error: unknown): number | null {
    return isHttpErrorResponse(error) ? error.status : null;
  }

  /**
   * This method should be called during app initialization.
   *
   * See: https://angular.io/api/core/APP_INITIALIZER
   */
  public initialize(): Observable<void> {
    const observable = new Observable<void>((subscriber) => {
      this.log("Initializing auth");

      this.oauthService.configure(authCodeFlowConfig);

      this.oauthService.events
        .pipe(
          filter(
            (event) =>
              event instanceof OAuthInfoEvent &&
              event.type === "token_expires" &&
              event.info === "access_token"
          ),
          debounceTime(1000)
        )
        .subscribe(() => {
          this.log("Triggering proactive refresh");
          this.refreshToken().subscribe();
        });

      const invitationCode = this.getParamFromUrl("invitation_code");
      if (invitationCode) {
        this.saveToLocalStorage("invitation_code", invitationCode);
      }

      const productTrialType = this.getParamFromUrl("product_trial_type");
      if (productTrialType) {
        this.saveToLocalStorage("product_trial_type", productTrialType);
      }

      const billingInterval = this.getParamFromUrl("billing_interval");
      if (billingInterval) {
        this.saveToLocalStorage("billing_interval", billingInterval);
      }

      const onboardInfo = this.getOnboardInfo();
      if (onboardInfo) {
        this.saveToLocalStorage("onboard_info", onboardInfo);
      }

      const pathname = window.location.pathname;

      const isRegisterLoginOidc = pathname.startsWith("/register-login-oidc");
      if (isRegisterLoginOidc) {
        subscriber.complete();
        return;
      }

      const isSigninOidc = pathname.startsWith("/signin-oidc");
      const error = this.getParamFromUrl("error");

      if (isSigninOidc && error) {
        this.router.navigateByUrl("/", {
          replaceUrl: true,
        });
        subscriber.complete();
        return;
      }

      this.oauthService
        .loadDiscoveryDocument()
        .then(() => this.oauthService.tryLoginCodeFlow())
        .then(() => {
          const hasValidAccessToken = this.oauthService.hasValidAccessToken();
          const hasRefreshToken = !!this.oauthService.getRefreshToken();

          if (!hasValidAccessToken && !hasRefreshToken) {
            return this.loginWithoutPrompt({
              // This is to prevent the state/nonce from being cleared
              disableClearingLocalStorageBeforeLogin: true,
            }).then(() => null);
          }

          if (hasValidAccessToken || !hasRefreshToken) {
            return Promise.resolve({ isAuthenticated: true });
          }

          return firstValueFrom(this.refreshToken())
            .then(() => {
              return Promise.resolve({ isAuthenticated: true });
            })
            .catch((error: unknown) => {
              // refreshToken() has already signed the user out if the auth
              // server rejected the grant.
              if (classifyRefreshFailure(error) === "session-ended") {
                return Promise.reject();
              }

              // The refresh could not reach the server — common when a
              // sleeping device wakes up before its network is ready. Loading
              // the app only helps if there is still an access token to send:
              // requests carry the expired token, get a 401, and the auth
              // interceptor refreshes once the network is back.
              //
              // With no access token at all there is nothing to send and
              // nothing to trigger a recovery, so requests would wait forever
              // on getAccessTokenObservable(). Sign in again instead.
              if (!this.oauthService.getAccessToken()) {
                this.logout(
                  "Could not refresh during app initialization and no access token remains"
                );
                return Promise.reject();
              }

              this.log(
                "Could not refresh during app initialization, continuing with the existing session"
              );
              return Promise.resolve({ isAuthenticated: true });
            });
        })
        .then((result) => {
          if (!result?.isAuthenticated) return;
          // By not completing this subscription, the application will NOT continue to load
          //
          subscriber.complete();
        })
        .catch(() => {
          subscriber.complete();
        });
    });

    return observable;
  }

  private beforeLogin(options?: LoginOptions) {
    // Don't remove invitation code as invite code may be for a new user
    const invitationCode = this.getInvitationCode();
    const productTrialType = this.getProductTrialType();
    const billingInterval = this.getBillingInterval();
    const isOnboarding = this.getIsOnboarding();

    // This is to support legacy code in the onboarding flow
    const onboardInfo = this.getOnboardInfo();

    if (!options?.disableClearingLocalStorageBeforeLogin) {
      localStorage.clear();
    }

    if (invitationCode) {
      this.saveToLocalStorage("invitation_code", invitationCode);
    }

    if (productTrialType) {
      this.saveToLocalStorage("product_trial_type", productTrialType);
    }

    if (billingInterval) {
      this.saveToLocalStorage("billing_interval", billingInterval);
    }

    if (isOnboarding) {
      this.saveToLocalStorage("is_onboarding", isOnboarding);
    }

    if (onboardInfo) {
      this.saveToLocalStorage("onboard_info", onboardInfo);
    }
  }

  public login(options?: LoginOptions) {
    this.beforeLogin(options);
    return this.oauthService.loadDiscoveryDocumentAndLogin();
  }

  public loginWithoutPrompt(options?: LoginOptions) {
    this.beforeLogin(options);
    this.oauthService.customQueryParams = {
      prompt: "none",
    };
    return this.oauthService.loadDiscoveryDocumentAndLogin();
  }

  public logout(reason: string, options?: { clientLogout?: boolean }) {
    this.appInsightsService.trackTrace("Logout", {
      reason,
    });

    this.appInsightsService
      .flush()
      .pipe(
        finalize(() => {
          if (options?.clientLogout) {
            window.location.href = this.oauthService.postLogoutRedirectUri;
            return;
          }
          this.oauthService.revokeTokenAndLogout({}, true);
        })
      )
      .subscribe();
  }

  public afterLogout() {
    sessionStorage.clear();
    localStorage.clear();
  }

  public ngOnDestroy(): void {
    if (this.storageListener) {
      window.removeEventListener("storage", this.storageListener);
    }
  }

  private handleOAuthEvent(event: OAuthEvent) {
    this.log("Event:", event);
    this.updateState();
  }

  private updateState() {
    this.isDepartmentAdminSubject.next(this.hasRole(Role.DepartmentAdmin));
    this.isBroadcasterSubject.next(this.hasRole(Role.Broadcaster));
    this.userIdSubject.next(this.getClaim<string>("sub"));

    // Only update the data region if there is an actual value, otherwise use whatever the current value is
    const dataRegion = this.getClaim<string>("data_region");
    if (dataRegion) this.dataRegionSubject.next(dataRegion);

    this.isAuthenticatedSubject.next(this.oauthService.hasValidAccessToken());
  }

  public getAccessToken() {
    return this.oauthService.getAccessToken();
  }

  /**
   * Returns an access token to send, refreshing first when the stored one has
   * expired.
   *
   * This is for callers that cannot rely on the auth interceptor noticing a
   * 401 and refreshing for them, such as the SignalR connection: it
   * authenticates once when it connects, so reconnecting after a device wakes
   * from sleep would otherwise present a token that expired while asleep and
   * fail until something else happened to refresh.
   *
   * A failed refresh still returns the stored token rather than throwing. The
   * caller has nothing better to send, and a rejected grant has already
   * started signing the user out.
   */
  public async getValidAccessToken(): Promise<string> {
    if (this.oauthService.hasValidAccessToken()) return this.getAccessToken();
    if (!this.getRefreshToken()) return this.getAccessToken();

    try {
      await firstValueFrom(this.refreshToken());
    } catch {
      this.log("getValidAccessToken: refresh failed, sending stored token");
    }

    return this.getAccessToken();
  }

  public hasValidAccessToken(): boolean {
    return this.oauthService.hasValidAccessToken();
  }

  public getAccessTokenObservable(): Observable<string> {
    // Immediately emit current access token, or wait until a token is available
    const observable = merge(
      of(this.getAccessToken()).pipe(filter((token) => !!token)),
      this.oauthService.events.pipe(
        filter((e) => e.type === "token_received"),
        map(() => this.getAccessToken()),
        filter((token) => !!token)
      )
    ).pipe(take(1));
    return observable;
  }

  public getRefreshToken() {
    return this.oauthService.getRefreshToken();
  }

  public getIdToken() {
    return this.oauthService.getIdToken();
  }

  public getClaims(): JWTPayload {
    const token = this.getAccessToken();
    if (typeof token !== "string") return {};
    return decodeJwt(token);
  }

  private parseClaim(claim: unknown): string[] {
    if (typeof claim === "string") {
      return [claim];
    } else if (Array.isArray(claim)) {
      return claim;
    }
    return [];
  }

  public getClaim<T = unknown>(key: string, required: true): T;
  public getClaim<T = unknown>(key: string): T | null;
  public getClaim<T = unknown>(
    key: string,
    required: boolean = false
  ): T | null {
    const claims = this.getClaims();
    const value = claims[key];

    if (required && (value === null || value === undefined)) {
      throw new Error(`Failed to get claim '${key}'`);
    }

    return (value ?? null) as T | null;
  }

  public getRoles(): string[] {
    const claims = this.getClaims();
    let roles = this.parseClaim(claims.role);
    return roles;
  }

  public hasRole(role: Role): boolean {
    return this.getRoles().includes(role);
  }

  public hasRoles(roles: Role[]): boolean {
    return roles.every((role) => this.hasRole(role));
  }

  public isForceCreatePassword(): boolean {
    return this.getClaim<string>("force_create_password") === "true";
  }

  public isAuthenticated(): boolean {
    this.updateState();
    return this.isAuthenticatedSubject.value;
  }

  public getUserId(): string | null {
    return (this.getClaims().sub ?? null) as string | null;
  }

  public getDataRegion(): string | null {
    return this.onboardingDataRegion ?? this.dataRegionSubject.value;
  }

  /**
   * This should only be used to set the data region when a user is onboarding.
   */
  public setOnboardingDataRegion(dataRegion: string) {
    this.onboardingDataRegion = dataRegion;
  }

  public setIsOnboarding(isOnboarding: boolean) {
    this.saveToLocalStorage("is_onboarding", `${isOnboarding}`);
  }

  public setHasCompletedOnboarding() {
    this.hasCompletedOnboardingSubject.next(true);
  }
}
